import React, { useState, useMemo } from 'react';
import Icon from '../../common/Icon/Icon';

/**
 * Date Picker component with calendar UI
 * @param {string} label - Label for the date picker
 * @param {Date} selectedDate - Currently selected date
 * @param {function} onDateSelect - Callback when date is selected
 * @param {array} disabledDates - Array of dates to disable
 * @param {Date} minDate - Minimum selectable date
 */
const DatePicker = ({
  label,
  selectedDate,
  onDateSelect,
  disabledDates = [],
  minDate = new Date(),
  className = '',
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const daysOfWeek = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const { days, prevMonthDays, nextMonthDays } = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // First day of month (0 = Sunday, 1 = Monday, etc.)
    const firstDay = new Date(year, month, 1).getDay();
    // Adjust for Monday start (0 = Monday, 6 = Sunday)
    const startDay = firstDay === 0 ? 6 : firstDay - 1;
    
    // Days in current month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Days in previous month
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    
    // Generate previous month days to fill
    const prevDays = [];
    for (let i = startDay - 1; i >= 0; i--) {
      prevDays.push(daysInPrevMonth - i);
    }
    
    // Generate current month days
    const currentDays = [];
    for (let i = 1; i <= daysInMonth; i++) {
      currentDays.push(i);
    }
    
    // Generate next month days to fill (to complete the grid)
    const totalDisplayed = prevDays.length + currentDays.length;
    const nextDays = [];
    const remaining = 7 - (totalDisplayed % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        nextDays.push(i);
      }
    }
    
    return {
      days: currentDays,
      prevMonthDays: prevDays,
      nextMonthDays: nextDays,
    };
  }, [currentMonth]);

  const isDateDisabled = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    
    // Check if before min date
    if (minDate && date < new Date(minDate.setHours(0, 0, 0, 0))) {
      return true;
    }
    
    // Check if in disabled dates
    return disabledDates.some(d => {
      const disabled = new Date(d);
      return disabled.toDateString() === date.toDateString();
    });
  };

  const isDateSelected = (day) => {
    if (!selectedDate) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return selectedDate.toDateString() === date.toDateString();
  };

  const handleDateClick = (day) => {
    if (!isDateDisabled(day)) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      onDateSelect?.(date);
    }
  };

  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="form-label">{label}</label>
      )}
      <div className="bg-surface-highlight border border-[#2e3f57] rounded-xl p-4 shadow-inner">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-white font-semibold flex items-center gap-2">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={goToPrevMonth}
              className="size-8 flex items-center justify-center hover:bg-surface rounded-full text-gray-400 transition-colors"
            >
              <Icon name="chevron_left" size="sm" />
            </button>
            <button
              type="button"
              onClick={goToNextMonth}
              className="size-8 flex items-center justify-center hover:bg-surface rounded-full text-gray-400 transition-colors"
            >
              <Icon name="chevron_right" size="sm" />
            </button>
          </div>
        </div>

        {/* Days of Week Header */}
        <div className="grid grid-cols-7 gap-1 text-center text-sm mb-2">
          {daysOfWeek.map((day, index) => (
            <div key={index} className="text-gray-500 text-[10px] uppercase font-bold py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 text-center text-sm">
          {/* Previous month days */}
          {prevMonthDays.map((day, index) => (
            <div key={`prev-${index}`} className="text-gray-700 py-2">
              {day}
            </div>
          ))}
          
          {/* Current month days */}
          {days.map((day) => {
            const disabled = isDateDisabled(day);
            const selected = isDateSelected(day);
            
            return (
              <div
                key={day}
                onClick={() => handleDateClick(day)}
                className={`
                  py-2 rounded-lg cursor-pointer transition-colors relative
                  ${disabled 
                    ? 'text-gray-500 line-through opacity-50 cursor-not-allowed' 
                    : 'text-white hover:bg-surface'
                  }
                  ${selected 
                    ? 'bg-primary shadow-lg shadow-blue-900/50 font-bold' 
                    : ''
                  }
                `}
              >
                {day}
                {/* Selected indicator */}
                {selected && (
                  <div className="absolute -top-1 -right-1 size-2 bg-gold rounded-full border border-surface-highlight" />
                )}
              </div>
            );
          })}
          
          {/* Next month days */}
          {nextMonthDays.map((day, index) => (
            <div key={`next-${index}`} className="text-gray-700 py-2">
              {day}
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default DatePicker;
